/**
 * PDF Viewer Component
 * Renders PDF pages using pdf.js
 */

import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Set worker source to local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

// Font info extracted from PDF
export interface FontInfo {
  fontFamily: string;
  fontSize: number;
  fontColor: string;
}

// Detected field from auto-scan
export interface DetectedField {
  fieldName: string;
  fieldType: 'text' | 'date' | 'currency' | 'number';
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  fontFamily: string;
  fontSize: number;
  confidence: number; // 0-1 confidence score
  detectedText: string; // The actual text found
}

// Exposed methods via ref
export interface PdfViewerRef {
  getFontInfoInRegion: (x: number, y: number, width: number, height: number, pageNum: number) => Promise<FontInfo | null>;
  autoDetectFields: () => Promise<DetectedField[]>;
}

interface PdfViewerProps {
  pdfUrl: string;
  pageNumber: number;
  scale: number;
  onPageRendered?: (dimensions: { width: number; height: number }) => void;
  onPageCountChange?: (count: number) => void;
}

export const PdfViewer = forwardRef<PdfViewerRef, PdfViewerProps>(({
  pdfUrl,
  pageNumber,
  scale,
  onPageRendered,
  onPageCountChange,
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const renderTaskRef = useRef<pdfjsLib.RenderTask | null>(null);

  // Map PDF font names to standard font families
  const mapFontName = (pdfFontName: string): string => {
    const fontLower = pdfFontName.toLowerCase();

    // Common mappings
    if (fontLower.includes('arial')) return fontLower.includes('bold') ? 'Arial-Bold' : 'Arial';
    if (fontLower.includes('helvetica')) return fontLower.includes('bold') ? 'Helvetica-Bold' : 'Helvetica';
    if (fontLower.includes('times')) return fontLower.includes('bold') ? 'Times-Bold' : 'Times-Roman';
    if (fontLower.includes('courier')) return fontLower.includes('bold') ? 'Courier-Bold' : 'Courier';
    if (fontLower.includes('georgia')) return 'Times-Roman';
    if (fontLower.includes('verdana')) return 'Helvetica';
    if (fontLower.includes('tahoma')) return 'Helvetica';
    if (fontLower.includes('calibri')) return 'Helvetica';
    if (fontLower.includes('cambria')) return 'Times-Roman';

    // Check for bold/italic indicators
    if (fontLower.includes('bold')) return 'Helvetica-Bold';

    // Default fallback
    return 'Helvetica';
  };

  // Get font information from a region of the PDF
  const getFontInfoInRegion = useCallback(async (
    x: number,
    y: number,
    width: number,
    height: number,
    pageNum: number
  ): Promise<FontInfo | null> => {
    if (!pdfDoc) return null;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const textContent = await page.getTextContent();
      const viewport = page.getViewport({ scale: 1 });

      // PDF coordinates have origin at bottom-left, but our coords are top-left
      // Convert our top-left coords to PDF bottom-left coords
      const pdfY = viewport.height - y - height;
      const pdfYEnd = viewport.height - y;

      // Find text items that overlap with the region
      const matchingItems: Array<{ fontName: string; fontSize: number }> = [];

      for (const item of textContent.items) {
        if ('transform' in item && 'str' in item) {
          const textItem = item as any;
          const tx = textItem.transform[4]; // x position
          const ty = textItem.transform[5]; // y position (PDF coords - bottom-left origin)
          const itemHeight = textItem.height || 12;
          const itemWidth = textItem.width || (textItem.str.length * 6);

          // Check if text item overlaps with our region
          const overlapsX = tx < (x + width) && (tx + itemWidth) > x;
          const overlapsY = ty < pdfYEnd && (ty + itemHeight) > pdfY;

          if (overlapsX && overlapsY && textItem.str.trim()) {
            matchingItems.push({
              fontName: textItem.fontName || 'g_d0_f1',
              fontSize: Math.abs(textItem.transform[0]) || 12, // Font size from transform matrix
            });
          }
        }
      }

      if (matchingItems.length === 0) {
        return null;
      }

      // Get the most common font in the region
      const fontCounts = new Map<string, { count: number; fontSize: number }>();
      for (const item of matchingItems) {
        const key = item.fontName;
        const existing = fontCounts.get(key);
        if (existing) {
          existing.count++;
        } else {
          fontCounts.set(key, { count: 1, fontSize: item.fontSize });
        }
      }

      // Find the most common font
      let maxCount = 0;
      let dominantFont = '';
      let dominantSize = 10;

      fontCounts.forEach((value, key) => {
        if (value.count > maxCount) {
          maxCount = value.count;
          dominantFont = key;
          dominantSize = value.fontSize;
        }
      });

      // Map the PDF font name to a standard font
      const mappedFont = mapFontName(dominantFont);

      return {
        fontFamily: mappedFont,
        fontSize: Math.round(dominantSize),
        fontColor: '#000000', // Default - PDF text color extraction is more complex
      };
    } catch (err) {
      console.error('Error extracting font info:', err);
      return null;
    }
  }, [pdfDoc]);

  // Pattern definitions for field detection
  const fieldPatterns = [
    // Account Number patterns
    {
      pattern: /(?:account\s*(?:number|#|no\.?)?:?\s*)(\d[\d\s\-*]{6,})/i,
      fieldName: 'accountNumber',
      fieldType: 'text' as const,
      labelPattern: /account\s*(?:number|#|no\.?)/i,
    },
    // Routing Number
    {
      pattern: /(?:routing\s*(?:number|#|no\.?)?:?\s*)(\d{9})/i,
      fieldName: 'routingNumber',
      fieldType: 'text' as const,
      labelPattern: /routing\s*(?:number|#|no\.?)/i,
    },
    // Statement Period - various formats
    {
      pattern: /(?:statement\s*period|for\s*(?:the\s*)?period):?\s*([A-Za-z]+\s+\d{1,2},?\s+\d{4}\s*(?:to|-|through)\s*[A-Za-z]+\s+\d{1,2},?\s+\d{4})/i,
      fieldName: 'statementPeriod',
      fieldType: 'text' as const,
      labelPattern: /statement\s*period|for\s*(?:the\s*)?period/i,
    },
    // Date patterns (MM/DD/YYYY, Month DD, YYYY)
    {
      pattern: /(?:statement\s*date|as\s*of|date):?\s*(\d{1,2}\/\d{1,2}\/\d{2,4}|[A-Za-z]+\s+\d{1,2},?\s+\d{4})/i,
      fieldName: 'statementPeriodEnd',
      fieldType: 'date' as const,
      labelPattern: /statement\s*date|as\s*of/i,
    },
    // Opening/Beginning Balance
    {
      pattern: /(?:opening|beginning|previous|starting)\s*balance:?\s*\$?([\d,]+\.?\d*)/i,
      fieldName: 'openingBalance',
      fieldType: 'currency' as const,
      labelPattern: /(?:opening|beginning|previous|starting)\s*balance/i,
    },
    // Closing/Ending Balance
    {
      pattern: /(?:closing|ending|current|new)\s*balance:?\s*\$?([\d,]+\.?\d*)/i,
      fieldName: 'closingBalance',
      fieldType: 'currency' as const,
      labelPattern: /(?:closing|ending|current|new)\s*balance/i,
    },
    // Total Deposits
    {
      pattern: /(?:total\s*)?deposits:?\s*\$?([\d,]+\.?\d*)/i,
      fieldName: 'totalDeposits',
      fieldType: 'currency' as const,
      labelPattern: /(?:total\s*)?deposits/i,
    },
    // Total Withdrawals
    {
      pattern: /(?:total\s*)?(?:withdrawals?|debits?):?\s*\$?([\d,]+\.?\d*)/i,
      fieldName: 'totalWithdrawals',
      fieldType: 'currency' as const,
      labelPattern: /(?:total\s*)?(?:withdrawals?|debits?)/i,
    },
    // Currency amounts (generic - for standalone amounts)
    {
      pattern: /\$\s*([\d,]+\.\d{2})/,
      fieldName: 'currency_generic',
      fieldType: 'currency' as const,
      labelPattern: null,
    },
  ];

  // Auto-detect fields from all pages
  const autoDetectFields = useCallback(async (): Promise<DetectedField[]> => {
    if (!pdfDoc) return [];

    const detectedFields: DetectedField[] = [];
    const numPages = pdfDoc.numPages;

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        // Build a map of text items with positions
        interface TextItem {
          str: string;
          x: number;
          y: number; // In top-left coordinate system
          width: number;
          height: number;
          fontName: string;
          fontSize: number;
        }

        const textItems: TextItem[] = [];

        for (const item of textContent.items) {
          if ('transform' in item && 'str' in item) {
            const ti = item as any;
            const x = ti.transform[4];
            const pdfY = ti.transform[5]; // PDF coords (bottom-left origin)
            const fontSize = Math.abs(ti.transform[0]) || 12;
            const height = ti.height || fontSize;
            const width = ti.width || (ti.str.length * fontSize * 0.5);

            // Convert to top-left coordinate system
            const y = viewport.height - pdfY - height;

            if (ti.str.trim()) {
              textItems.push({
                str: ti.str,
                x,
                y,
                width,
                height,
                fontName: ti.fontName || '',
                fontSize,
              });
            }
          }
        }

        // Group nearby text items into lines
        const lines: { text: string; items: TextItem[] }[] = [];
        const sortedItems = [...textItems].sort((a, b) => {
          const yDiff = a.y - b.y;
          if (Math.abs(yDiff) > 5) return yDiff;
          return a.x - b.x;
        });

        let currentLine: TextItem[] = [];
        let lastY = -100;

        for (const item of sortedItems) {
          if (Math.abs(item.y - lastY) > 8) {
            if (currentLine.length > 0) {
              const lineText = currentLine.map(i => i.str).join(' ');
              lines.push({ text: lineText, items: currentLine });
            }
            currentLine = [item];
            lastY = item.y;
          } else {
            currentLine.push(item);
            lastY = (lastY + item.y) / 2;
          }
        }
        if (currentLine.length > 0) {
          lines.push({ text: currentLine.map(i => i.str).join(' '), items: currentLine });
        }

        // Search for patterns in the lines
        for (const line of lines) {
          for (const patternDef of fieldPatterns) {
            const match = line.text.match(patternDef.pattern);
            if (match) {
              // Skip generic currency if we already have specific matches
              if (patternDef.fieldName === 'currency_generic') {
                continue; // Skip generic matches for now
              }

              // Find the bounding box for the matched value
              const valueText = match[1];
              const lineItems = line.items;

              // Find items that contain the matched value
              let minX = Infinity, maxX = 0, minY = Infinity, maxY = 0;
              let foundItems: TextItem[] = [];

              // Look for the value in the line items
              for (const item of lineItems) {
                if (valueText.includes(item.str.trim()) || item.str.includes(valueText)) {
                  foundItems.push(item);
                }
              }

              // If we didn't find specific items, use all items after the label
              if (foundItems.length === 0) {
                // Find where the label ends and value begins
                const labelMatch = patternDef.labelPattern ? line.text.match(patternDef.labelPattern) : null;
                if (labelMatch) {
                  const labelEnd = line.text.indexOf(labelMatch[0]) + labelMatch[0].length;
                  // Take items that appear after the label
                  let charPos = 0;
                  for (const item of lineItems) {
                    if (charPos >= labelEnd) {
                      foundItems.push(item);
                    }
                    charPos += item.str.length + 1;
                  }
                }
              }

              // If still no items, use the last items in the line (likely the value)
              if (foundItems.length === 0 && lineItems.length > 0) {
                foundItems = lineItems.slice(-Math.min(3, lineItems.length));
              }

              // Calculate bounding box
              for (const item of foundItems) {
                minX = Math.min(minX, item.x);
                maxX = Math.max(maxX, item.x + item.width);
                minY = Math.min(minY, item.y);
                maxY = Math.max(maxY, item.y + item.height);
              }

              if (minX !== Infinity) {
                // Get font info from first found item
                const firstItem = foundItems[0] || lineItems[0];
                const mappedFont = mapFontName(firstItem?.fontName || '');

                // Add padding
                const padding = 2;

                detectedFields.push({
                  fieldName: patternDef.fieldName,
                  fieldType: patternDef.fieldType,
                  pageNumber: pageNum,
                  x: minX - padding,
                  y: minY - padding,
                  width: (maxX - minX) + (padding * 2),
                  height: (maxY - minY) + (padding * 2),
                  fontFamily: mappedFont,
                  fontSize: Math.round(firstItem?.fontSize || 10),
                  confidence: 0.8,
                  detectedText: valueText,
                });
              }
            }
          }
        }

        // Also try to detect name/address blocks (typically at top left)
        // Look for multi-line text blocks that could be addresses
        const addressLines: TextItem[][] = [];
        let currentBlock: TextItem[] = [];
        let lastBlockY = -100;

        for (const item of sortedItems) {
          // Look for text in typical address area (left side, upper portion)
          if (item.x < viewport.width * 0.5 && item.y < viewport.height * 0.5) {
            if (Math.abs(item.y - lastBlockY) < 15) {
              currentBlock.push(item);
            } else {
              if (currentBlock.length > 0) {
                addressLines.push(currentBlock);
              }
              currentBlock = [item];
            }
            lastBlockY = item.y;
          }
        }
        if (currentBlock.length > 0) {
          addressLines.push(currentBlock);
        }

        // Check if we have what looks like an address block (2-4 consecutive lines)
        if (addressLines.length >= 2 && addressLines.length <= 5 && pageNum === 1) {
          // Check if any line looks like a name (no numbers at start, reasonable length)
          for (let i = 0; i < Math.min(addressLines.length, 4); i++) {
            const lineText = addressLines[i].map(t => t.str).join(' ');
            const firstItem = addressLines[i][0];

            // First line might be account holder name
            if (i === 0 && !lineText.match(/^\d/) && lineText.length > 3 && lineText.length < 50) {
              const minX = Math.min(...addressLines[i].map(t => t.x));
              const maxX = Math.max(...addressLines[i].map(t => t.x + t.width));
              const minY = Math.min(...addressLines[i].map(t => t.y));
              const maxY = Math.max(...addressLines[i].map(t => t.y + t.height));

              // Check if this looks like a name (contains letters, not a bank name pattern)
              if (!lineText.match(/bank|credit union|financial/i)) {
                detectedFields.push({
                  fieldName: 'accountHolderName',
                  fieldType: 'text',
                  pageNumber: pageNum,
                  x: minX - 2,
                  y: minY - 2,
                  width: (maxX - minX) + 4,
                  height: (maxY - minY) + 4,
                  fontFamily: mapFontName(firstItem?.fontName || ''),
                  fontSize: Math.round(firstItem?.fontSize || 10),
                  confidence: 0.6,
                  detectedText: lineText,
                });
              }
            }

            // Lines 2-4 might be address
            if (i >= 1 && i <= 3) {
              const minX = Math.min(...addressLines[i].map(t => t.x));
              const maxX = Math.max(...addressLines[i].map(t => t.x + t.width));
              const minY = Math.min(...addressLines[i].map(t => t.y));
              const maxY = Math.max(...addressLines[i].map(t => t.y + t.height));

              const fieldName = `accountHolderAddressLine${i}` as string;

              detectedFields.push({
                fieldName,
                fieldType: 'text',
                pageNumber: pageNum,
                x: minX - 2,
                y: minY - 2,
                width: (maxX - minX) + 4,
                height: (maxY - minY) + 4,
                fontFamily: mapFontName(firstItem?.fontName || ''),
                fontSize: Math.round(firstItem?.fontSize || 10),
                confidence: 0.5,
                detectedText: lineText,
              });
            }
          }
        }

      } catch (err) {
        console.error(`Error scanning page ${pageNum}:`, err);
      }
    }

    // Remove duplicates (same field detected multiple times)
    const uniqueFields = new Map<string, DetectedField>();
    for (const field of detectedFields) {
      const key = `${field.fieldName}_${field.pageNumber}`;
      const existing = uniqueFields.get(key);
      if (!existing || field.confidence > existing.confidence) {
        uniqueFields.set(key, field);
      }
    }

    return Array.from(uniqueFields.values());
  }, [pdfDoc, mapFontName]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    getFontInfoInRegion,
    autoDetectFields,
  }), [getFontInfoInRegion, autoDetectFields]);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);

      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;

        if (!cancelled) {
          setPdfDoc(pdf);
          onPageCountChange?.(pdf.numPages);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading PDF:', err);
          setError('Failed to load PDF');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl, onPageCountChange]);

  // Render page
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    // Cancel any ongoing render
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    try {
      const page = await pdfDoc.getPage(pageNumber);
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      renderTaskRef.current = page.render(renderContext);

      await renderTaskRef.current.promise;

      // Return UNSCALED dimensions (base PDF size at scale=1)
      const baseViewport = page.getViewport({ scale: 1 });
      onPageRendered?.({
        width: baseViewport.width,
        height: baseViewport.height,
      });
    } catch (err: any) {
      if (err?.name !== 'RenderingCancelledException') {
        console.error('Error rendering page:', err);
      }
    }
  }, [pdfDoc, pageNumber, scale, onPageRendered]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-red-50">
        <div className="text-center text-red-600">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="block"
      style={{ imageRendering: 'auto' }}
    />
  );
});

PdfViewer.displayName = 'PdfViewer';

export default PdfViewer;
